import { useState } from "react";
import { toast } from "sonner";
import { Activity, KeyRound, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	revokeApiKey,
	regenerateApiKey,
	type ApiKey,
	type ApiKeyScope,
} from "@/services/api-key.service";

function formatDateTime(value: string | null): string {
	if (!value) return "—";
	return new Date(value).toLocaleString();
}

function formatDate(value: string | null): string {
	if (!value) return "—";
	return new Date(value).toLocaleDateString();
}

function statusBadge(key: ApiKey): { label: string; cls: string } {
	if (key.revoked_at) {
		return {
			label: "Revoked",
			cls: "bg-danger-subtle text-danger border-danger/20",
		};
	}
	if (key.expires_at && new Date(key.expires_at).getTime() <= Date.now()) {
		return {
			label: "Expired",
			cls: "bg-muted-subtle text-muted-foreground border-border",
		};
	}
	return {
		label: "Active",
		cls: "bg-success-subtle text-success border-success/20",
	};
}

export function ProfileApiKeysList({
	keys,
	setReveal,
	onRevoked,
	onRegenerated,
	onViewUsage,
}: {
	keys: ApiKey[];
	setReveal: (key: string) => void;
	onRevoked: (id: string) => void;
	/** New prefix after a regenerate, so the row updates through state. */
	onRegenerated: (id: string, keyPrefix: string) => void;
	// Optional so existing call sites that do not render a usage dialog keep
	// working unchanged; the History button is simply not shown for them.
	onViewUsage?: (key: ApiKey) => void;
}) {
	const [revoking, setRevoking] = useState<string | null>(null);
	const [regenerating, setRegenerating] = useState<string | null>(null);

	// An existing key cannot be shown again: only its hash is stored. Revealing
	// one therefore means replacing it, which is what this says up front.
	async function handleRegenerate(key: ApiKey) {
		const ok = window.confirm(
			`Regenerate "${key.name}"?\n\nThis creates a brand new key and the current one stops working immediately. Any external app using it must be updated with the new key in full.\n\nThe new key is shown once, so copy it before closing.`,
		);
		if (!ok) return;
		setRegenerating(key.id);
		try {
			const res = await regenerateApiKey(key.id);
			const plaintext = res?.plaintext || "";
			setReveal(plaintext);
			onRegenerated(key.id, plaintext.slice(0, 16));

			// Best effort: the dialog shows the key regardless, and the clipboard
			// is unavailable outside a secure context or without permission.
			try {
				await navigator.clipboard.writeText(plaintext);
				toast.success("New API key generated and copied to clipboard.");
			} catch {
				toast.success("New API key generated. Copy it from the dialog.");
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to regenerate key.",
			);
		} finally {
			setRegenerating(null);
		}
	}
	async function handleRevoke(key: ApiKey) {
		if (key.revoked_at) return;
		const ok = window.confirm(
			`Revoke "${key.name}"? External apps using this key will stop working immediately.`,
		);
		if (!ok) return;
		setRevoking(key.id);
		try {
			await revokeApiKey(key.id);
			toast.success("API key revoked.");
			onRevoked(key.id);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to revoke key.",
			);
		} finally {
			setRevoking(null);
		}
	}

	if (keys.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-8 text-center">
				<KeyRound className="h-6 w-6 text-muted-foreground" />
				<p className="text-sm font-medium text-foreground">
					No API keys yet
				</p>
				<p className="max-w-sm text-xs text-muted-foreground">
					Generate a key to let an external app create tickets or
					tasks in one of your projects.
				</p>
			</div>
		);
	}

	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full text-left text-xs">
				<thead className="border-b border-border bg-muted-subtle text-muted-foreground">
					<tr>
						<th className="whitespace-nowrap px-3 py-2 font-medium">Name</th>
						<th className="whitespace-nowrap px-3 py-2 font-medium">Project</th>
						<th className="whitespace-nowrap px-3 py-2 font-medium">Key</th>
						<th className="whitespace-nowrap px-3 py-2 font-medium">Scopes</th>
						<th className="whitespace-nowrap px-3 py-2 font-medium">Last used</th>
						<th className="whitespace-nowrap px-3 py-2 font-medium">Expires</th>
						<th className="whitespace-nowrap px-3 py-2 font-medium">Status</th>
						<th className="whitespace-nowrap px-3 py-2" />
					</tr>
				</thead>
				<tbody>
					{keys.map((k) => {
						const badge = statusBadge(k);

						return (
							<tr
								key={k.id}
								className="border-b border-border last:border-b-0"
							>
								<td className="whitespace-nowrap px-3 py-2 font-medium text-foreground">
									{k.name}
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
									{k.project?.name ?? "—"}
								</td>
								<td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-foreground">
									{k.key_prefix}…
								</td>
								<td className="px-3 py-2">
									<div className="flex flex-wrap gap-1">
										{k.scopes.map((s: ApiKeyScope) => (
											<span
												key={s}
												className="rounded-md bg-muted-subtle px-1.5 py-0.5 text-[10px] text-muted-foreground"
											>
												{s}
											</span>
										))}
									</div>
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
									{formatDateTime(k.last_used_at)}
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
									{formatDate(k.expires_at)}
								</td>
								<td className="whitespace-nowrap px-3 py-2">
									<span
										className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}
									>
										{badge.label}
									</span>
								</td>
								<td className="whitespace-nowrap px-3 py-2 gap-2 flex text-right">
									{onViewUsage && (
										<Button
											type="button"
											variant="outline"
											size="xs"
											onClick={() => onViewUsage(k)}
											title="See every request made with this key."
										>
											<Activity className="h-3 w-3" />
											History
										</Button>
									)}
									{!k.revoked_at && (
										<>
											<Button
												type="button"
												variant="primary_outline"
												size="xs"
												disabled={regenerating === k.id}
												onClick={() => handleRegenerate(k)}
												title="Creates a new key and stops the current one working. External apps using it must be updated."
											>
												<RefreshCw className="h-3 w-3" />
												{regenerating === k.id
													? "Regenerating…"
													: "Regenerate"}
											</Button>
											<Button
												type="button"
												variant="destructive_outline"
												size="xs"
												disabled={revoking === k.id}
												onClick={() => handleRevoke(k)}
											>
												<Trash2 className="h-3 w-3" />
												{revoking === k.id
													? "Revoking…"
													: "Revoke"}
											</Button>
										</>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
